import type { FC } from "react";
import type { Photo, Video } from "../../types";
import { Button } from "../Button";
import CloseIcon from "../../icons/close.svg?react";
import { useI18n } from "../../i18n";

import styles from "./PhotoGallery.module.css";

type Props = {
  items: Array<Photo | Video>;
  onClick: (item: Photo | Video, index: number) => void;
  open?: boolean;
  title?: string;
  onClose?: () => void;
};

export const PhotoGallery: FC<Props> = ({
  items,
  onClick,
  open = true,
  title,
  onClose,
}) => {
  const i18n = useI18n();
  const resolvedTitle = title ?? i18n.assistant.galleryTitle;
  const resolvePreviewSrc = (item: Photo | Video) => {
    if (item.type === "video") {
      return item.thumbnailSrc ?? item.src;
    }
    return item.thumbnailSrc ?? item.src;
  };

  if (!open) {
    return null;
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.title}>{resolvedTitle}</div>
          {onClose && (
            <Button className={styles.close} onClick={onClose}>
              <CloseIcon />
            </Button>
          )}
        </div>
        <div className={styles.body}>
          <div className={styles.grid}>
            {items.map((item, index) => (
              <figure
                key={item.id}
                className={styles.figure}
                onClick={() => onClick(item, index)}
              >
                <img
                  className={styles.img}
                  src={resolvePreviewSrc(item)}
                  width={item.type === "photo" ? item.width : undefined}
                  height={item.type === "photo" ? item.height : undefined}
                  alt={item.caption}
                />
                {item.caption && (
                  <figcaption className={styles.caption}>
                    {item.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
